/* /home/dev/Projects/sandbox/three-wfc/lib/WFC2D.ts */
import { Color, Vector2 } from "three";
import { WFCTile2D } from "./WFCTile2D";
import { WFC2DBuffer } from "./WFC2DBuffer";

export class WFC2D {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  size: number; // Cell size in pixels
  drawGrid: boolean = false;
  wfcBuffer!: WFC2DBuffer;
  seed?: number | undefined;
  readonly offset: Vector2 = new Vector2(); // Optional drawing offset
  readonly tiles: WFCTile2D[] = []; // Original tiles added by the user
  private ctx: CanvasRenderingContext2D;

  constructor({
    canvas,
    width,
    height,
    cellSize,
    seed,
    drawGrid,
  }: {
    canvas: HTMLCanvasElement;
    width: number; // Width of the area to fill (in pixels or units)
    height: number; // Height of the area to fill (in pixels or units)
    cellSize: number; // Size of one tile/cell (in pixels or units)
    seed?: number;
    drawGrid?: boolean;
  }) {
    this.canvas = canvas;
    this.width = width;
    this.height = height;
    this.size = cellSize;
    this.ctx = this.canvas.getContext("2d") as CanvasRenderingContext2D;

    this.canvas.width = width;
    this.canvas.height = height;

    if (seed) this.seed = seed;
    this.drawGrid = !!drawGrid;
  }

  addTile(...tiles: WFCTile2D[]) {
    this.tiles.push(...tiles);
  }

  removeTile(...tiles: WFCTile2D[]) {
    tiles.forEach((tile) => {
      const index = this.tiles.indexOf(tile);
      if (!~index) return;
      this.tiles.splice(index, 1);
    });
  }

  clear() {
    this.tiles.length = 0;
  }

  init() {
    // Calculate grid dimensions based on area and cell size
    const cols = Math.ceil(this.width / this.size);
    const rows = Math.ceil(this.height / this.size);

    // Ensure canvas physical pixels match logical grid * cell size
    // This assumes the canvas should exactly fit the grid.
    this.canvas.width = cols * this.size;
    this.canvas.height = rows * this.size;

    // Pass the original tiles; WFC2DBuffer's constructor handles expansion via WFCTileBuffer
    this.wfcBuffer = new WFC2DBuffer(this.tiles, cols, rows, this.seed);
  }

  collapseAll() {
    if (!this.wfcBuffer) {
      console.error("WFC not initialized. Call init() first.");
      return false;
    }
    // Assuming collapseAll returns a boolean indicating success/failure
    return this.wfcBuffer.collapseAll();
  }

  collapse() {
    if (!this.wfcBuffer) {
      console.error("WFC not initialized. Call init() first.");
      return false;
    }
    return this.wfcBuffer.collapse();
  }

  collapseCell(index: number) {
    if (!this.wfcBuffer) {
      console.error("WFC not initialized. Call init() first.");
      return false; // Return boolean to indicate success/failure
    }
    return this.wfcBuffer.collapseCell(index);
  }

  /**
   * Draws the current state of the WFC grid onto the canvas.
   * Applies transformations (rotation, reflection) dynamically for debugging.
   */
  draw() {
    const ctx = this.ctx;
    const wfcBuffer = this.wfcBuffer; // Cache for slightly better access
    const cols = wfcBuffer.cols;
    const rows = wfcBuffer.rows;
    const tiles = this.tiles;
    const cellSize = this.size;
    const offsetX = this.offset.x;
    const offsetY = this.offset.y;
    const grid = this.drawGrid;

    // Clear the canvas before drawing the new state
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.strokeStyle = "#555"; // Default stroke style for uncollapsed cells

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const index = y * cols + x;
        const drawX = x * cellSize + offsetX;
        const drawY = y * cellSize + offsetY;

        // Check collapsed state using the buffer's array directly
        if (wfcBuffer.collapsed[index] !== -1) {
          const tileIndex = wfcBuffer.getCollapsedTile(index);

          // *** UPDATED: Get the tile from the WFCBuffer's internal tile list ***
          const tile = tileIndex !== -1 ? tiles[tileIndex] : null;

          if (tile && tile.image) {
            const content = tile.image;
            const needsTransform =
              tile._rotation !== 0 || tile._reflectX || tile._reflectY;

            if (needsTransform) {
              ctx.save(); // Save context state

              // Translate origin to the center of the cell
              const centerX = drawX + cellSize / 2;
              const centerY = drawY + cellSize / 2;
              ctx.translate(centerX, centerY);

              // Apply rotation (clockwise)
              if (tile._rotation > 0) {
                ctx.rotate((tile._rotation * Math.PI) / 2);
              }

              // Apply reflection (scale)
              if (tile._reflectX || tile._reflectY) {
                ctx.scale(tile._reflectX ? -1 : 1, tile._reflectY ? -1 : 1);
              }

              // Draw content centered around the new (transformed) origin
              const halfSize = cellSize / 2;

              // Check if content is Color (from three.js) - unlikely with current WFCTile2D, but keep for robustness
              if (content instanceof Color) {
                ctx.fillStyle = `#${content.getHexString()}`;
                // Draw rect centered at the transformed origin
                ctx.fillRect(-halfSize, -halfSize, cellSize, cellSize);
              } else {
                // Image or Canvas
                // Draw image centered at the transformed origin
                ctx.drawImage(
                  content,
                  -halfSize,
                  -halfSize,
                  cellSize,
                  cellSize
                );
              }

              ctx.restore(); // Restore context state
            } else {
              // No transformation needed, draw directly at top-left corner
              if (content instanceof Color) {
                ctx.fillStyle = `#${content.getHexString()}`;
                ctx.fillRect(drawX, drawY, cellSize, cellSize);
              } else {
                // Image or Canvas
                ctx.drawImage(content, drawX, drawY, cellSize, cellSize);
              }
            }

            if (grid) ctx.strokeRect(drawX, drawY, cellSize, cellSize);
          } else {
            // Fallback if tile or content is missing for a collapsed cell (error state)
            ctx.fillStyle = "magenta"; // Use a different error color for clarity
            ctx.fillRect(drawX, drawY, cellSize, cellSize);
            console.error(
              `Collapsed cell [${x}, ${y}] (index ${index}) has invalid tile index ${tileIndex} or missing content.`
            );
          }
        } else {
          // Cell is not collapsed, draw a placeholder
          ctx.strokeRect(drawX, drawY, cellSize, cellSize);

          // Visualize entropy/number of options
          const numOptions = wfcBuffer.options.optionsCount(index);
          const maxOptions = wfcBuffer.tiles.count; // Total possible tiles including transformations
          const entropyFactor = numOptions / maxOptions; // Simple ratio, not actual entropy

          // Fade from light grey (many options) to dark grey (few options)
          const greyValue = Math.floor(50 + entropyFactor * 100); // Adjusted range for better visibility on dark bg
          ctx.fillStyle = `rgba(${greyValue}, ${greyValue}, ${greyValue}, 0.7)`;
          ctx.fillRect(drawX, drawY, cellSize, cellSize);

          // Optional: Draw number of options
          ctx.fillStyle = "white"; // White text on grey background
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = `bold ${cellSize * 0.4}px sans-serif`; // Slightly bolder font
          ctx.fillText(
            numOptions.toString(),
            drawX + cellSize / 2,
            drawY + cellSize / 2
          );

          // Add small index drawing at the top-left corner
          ctx.fillStyle = "#cccccc"; // Lighter grey for index
          ctx.textAlign = "left";
          ctx.textBaseline = "top";
          ctx.font = `${cellSize * 0.2}px sans-serif`; // Smaller font size for index
          ctx.fillText(
            index.toString(), // Draw the cell's linear index
            drawX + 2, // Small padding from the left edge
            drawY + 2 // Small padding from the top edge
          );
        }
      }
    }
  }
}
